import type { Column, Header, Row } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import type { Optional } from '@zen/utils/typescript';

export interface GroupByResult {
  key: string;
  label: ReactNode;
}

export type GroupByValue = Optional<GroupByResult | string>;

export interface GroupHeaderMeta<T> {
  groupKey: string;
  isCollapsed: boolean;
  itemCount: number;
  items: T[];
  label: ReactNode;
  toggleCollapse: () => void;
}

export interface RowAttributes<T> {
  disabled?: boolean;
  expanded?: boolean;
  id?: string;
  nestedRowItems?: T[];
}

export type TableRowData<T> = RowAttributes<T> | {};

export type CellContentPlacement = 'top-start' | 'right' | 'left';

/**
 * Minimal interface for table header props used by TableHeaderCell.
 * This allows passing headers with any data type without type conflicts.
 */
export interface TableHeaderProps {
  column: {
    getIsResizing: () => boolean;
    resetSize: () => void;
  };
  getResizeHandler: () => (event: unknown) => void;
  getSize: () => number;
}

// Full TanStack types for internal use
export type FullHeader<T> = Header<T, unknown>;
export type FullRow<T> = Row<T>;
export type FullColumn<T> = Column<T, unknown>;

export interface TableColumn<T = {}> {
  alignment?: CellContentPlacement;
  bulkSelection?: boolean;
  className?: string;
  dataKey?: string;
  ellipsis?: boolean;
  fixed?: 'left' | 'right';
  key: string;
  mergeBy?: (record: T, index: number) => string;
  render?: (value: unknown, record: T, index: number) => ReactNode;
  resizable?: boolean;
  sortKey?: string;
  sortable?: boolean;
  title?: ReactNode;
  width?: number | string;
}

export type TableColumnOrder = Pick<TableColumn, 'fixed' | 'key'>;

export type RowSpan = { atIndex: number; value: number };
export type RowSpanConfiguration = Record<string, RowSpan[]>;

export interface TotalCountConfig {
  entityName?: string;
  isLoading?: boolean;
  totalCount: number;
}

export const TableConfigurationTrackingCategory = 'TableConfiguration';

export enum TableConfigurationTrackingAction {
  CUSTOMISE = 'CustomiseTableColumn',
  REORDER = 'ReorderTableColumn',
  RESIZE = 'ResizeTableColumn',
  SORT = 'SortTableColumn'
}

// TanStack Table meta types
export interface TanStackColumnMeta<T> {
  alignment?: CellContentPlacement;
  bulkSelection?: boolean;
  className?: string;
  columnKey: string;
  ellipsis?: boolean;
  fixed?: 'left' | 'right';
  isPartOfMergeByColumn: boolean;
  originalConfig: TableColumn<T>;
  resizable: boolean;
  rowSpans?: RowSpan[];
  sortKey: string;
  sortable: boolean;
  width?: number | string;
}

export type DataWithRowId<T, K = T> = ExpandableData<T, K> & { uniqueTableRowIdentifier: string };
export type ExpandableData<T, K = T> = T & { nestedRowItems?: K[] };
